import type { TypeFlowFailure, TypeFlowMessage, TypeFlowScreen } from '@/types/auth/types.auth.flow';

const CODE_SCREENS = new Set<TypeFlowScreen['id']>(['email-code', 'totp', 'recovery-code', 'totp-setup']);

const wait = (error: TypeFlowFailure): string => {
    const seconds = error.retryAfterSeconds;
    if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return 'fra qualche minuto';
    if (seconds < 90) return `fra ${Math.ceil(seconds)} secondi`;
    return `fra ${Math.ceil(seconds / 60)} minuti`;
};

const credentialText = (screen: TypeFlowScreen['id']): TypeFlowMessage => {
    if (screen === 'password') return { field: 'password', text: 'Email o password non corretti.' };
    if (screen === 'new-password') return { field: 'password', text: 'Questa password non è accettata: scegline una più lunga o meno comune.' };
    if (screen === 'passkey') return { field: null, text: 'La passkey non è stata riconosciuta.' };
    if (CODE_SCREENS.has(screen)) return { field: 'code', text: 'Codice non valido o scaduto.' };
    return { field: null, text: 'I dati inseriti non sono corretti.' };
};

const FIELD_ALIASES: Record<string, string> = { hint: 'email', identifier: 'email', newPassword: 'password' };

export default function flowMessageOf(error: TypeFlowFailure, screen: TypeFlowScreen['id']): TypeFlowMessage {
    switch (error.code) {
        case 'CREDENTIAL_INVALID': return credentialText(screen);
        case 'INPUT_INVALID': {
            const raw = Object.keys(error.fields ?? {})[0] ?? null;
            const field = raw === null ? null : Object.prototype.hasOwnProperty.call(FIELD_ALIASES, raw) ? FIELD_ALIASES[raw] : raw;
            if (field === 'email') return { field, text: 'Indirizzo email non valido.' };
            if (field === 'password') return { field, text: 'La password deve avere almeno 12 caratteri.' };
            return { field: field ?? (CODE_SCREENS.has(screen) ? 'code' : null), text: 'Controlla il valore inserito.' };
        }
        case 'ATTEMPTS_EXHAUSTED': return { field: CODE_SCREENS.has(screen) ? 'code' : screen === 'password' ? 'password' : null, text: `Troppi tentativi sbagliati. Riprova ${wait(error)}.` };
        case 'RATE_LIMITED': return { field: null, text: `Troppe richieste ravvicinate. Riprova ${wait(error)}.` };
        case 'FLOW_NOT_FOUND': return { field: null, text: 'La procedura è scaduta o è stata aperta in un’altra scheda. Ricomincia.' };
        case 'STEP_NOT_ALLOWED': return { field: null, text: 'Questo passaggio non è più disponibile. Ricomincia.' };
        case 'FORBIDDEN': return { field: null, text: 'Questo account non può accedere alla dashboard. Chiedi all’amministratore.' };
        case 'SIGN_UP_CLOSED': return { field: null, text: 'Alla dashboard non ci si iscrive: l’account lo crea l’amministratore.' };
        case 'ALREADY_EXISTS': return { field: null, text: 'Esiste già un account con questa email. Accedi con le tue credenziali.' };
        case 'UNAUTHENTICATED':
        case 'SESSION_REVOKED': return { field: null, text: 'La tua sessione non è più valida: accedi di nuovo.' };
        case 'EXPIRED': return { field: CODE_SCREENS.has(screen) ? 'code' : null, text: 'Il link o il codice è scaduto: chiedine uno nuovo.' };
        case 'AUTH_UNAVAILABLE':
        case 'UPSTREAM_UNAVAILABLE': return { field: null, text: 'Il servizio di accesso non risponde. Riprova fra poco.' };
        default: return { field: null, text: 'Qualcosa non è andato a buon fine. Riprova.' };
    }
}
